export const toNumber = (v: any) => {
    if (v === null || v === undefined || v === '') return 0;
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    const normalized = String(v).replace(',', '.');
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
};

// Allows partial decimal input as the user types (e.g. "1", "1,", "1,5")
// with either "," or "." as the separator, regardless of browser locale.
export const DECIMAL_INPUT_REGEX = /^\d*[.,]?\d*$/;

// ---------- ROUND ----------
// Must match exactly the backend calculs.ts round function
const round = (value: any) => {
    let num = toNumber(value);
    if (!Number.isFinite(num)) return 0;
    num = parseFloat(num.toFixed(4)); // Avoid floating point errors

    // For values between 0 and 1, temporarily shift to avoid Math.ceil asymmetry
    const isZero = num.toString()[0] === '0';
    if (isZero) num += 1;
    num = Math.ceil(num * 100) / 100;
    if (isZero) num -= 1;

    return parseFloat(num.toFixed(2));
};

// ---------- IS CUSTOMER HT ----------
export const isCustomerHT = (customer: any): boolean =>
    customer?.isHT === true ||
    customer?.isHT === 'true' ||
    customer?.isHT === 1 ||
    customer?.isHT === '1' ||
    customer?.isHt === true ||
    customer?.isHt === 'true';

// ---------- LINE TOTAL HT ----------
export const getInvoiceLineTotalHT = (items: any[]) => {
    const totalHT = items.reduce((acc: number, item: any) => {
        const price = toNumber(item.price);
        const quantity = toNumber(item.quantity);
        const discountValue = toNumber(item.discountValue);

        let discount = 0;
        if (item.discountType === "percent")
            discount = price * quantity * (discountValue / 100);
        else if (item.discountType === "value")
            discount = discountValue;

        const lineTotal = price * quantity - discount;
        return acc + lineTotal;
    }, 0);

    return round(totalHT);
};

interface GlobalDiscount {
    globalDiscountType?: string; // 'none' | 'percent' | 'value'
    globalDiscountValue?: number;
}

// ---------- RAW TOTAL HT ----------
// Sum of per-line HT, before any order-level (global) discount
const getRawTotalHT = (items: any) => {
    let totalHT = 0;
    for (const item of items) {
        totalHT += getInvoiceLineTotalHT([item]);
    }
    return round(totalHT);
};

// ---------- GLOBAL DISCOUNT AMOUNT ----------
// Resolves a global discount (percent or fixed value) to an HT euro amount,
// capped so it can never exceed (or invert) the lines' raw HT total.
// Must match exactly the backend calculs.ts getGlobalDiscountAmount function.
export const getGlobalDiscountAmount = (
    items: any,
    globalDiscountType?: string,
    globalDiscountValue?: number,
) => {
    if (!globalDiscountType || globalDiscountType === 'none' || !globalDiscountValue) return 0;

    const rawTotalHT = getRawTotalHT(items);
    let discount = 0;
    if (globalDiscountType === 'percent') discount = rawTotalHT * (toNumber(globalDiscountValue) / 100);
    else if (globalDiscountType === 'value') discount = toNumber(globalDiscountValue);

    // Clamp toward zero on whichever side rawTotalHT sits (usually [0, rawTotalHT],
    // but rawTotalHT can be negative for a credit note built from negated lines —
    // there the clamp range flips to [rawTotalHT, 0] so a negative "reversal"
    // discount still resolves correctly instead of being floored to 0).
    const lo = Math.min(0, rawTotalHT);
    const hi = Math.max(0, rawTotalHT);
    discount = Math.min(Math.max(discount, lo), hi);
    return round(discount);
};

// ---------- TOTAL HT ----------
export const getTotalHT = (items: any, discount?: GlobalDiscount) => {
    const rawTotalHT = getRawTotalHT(items);
    const discountAmount = getGlobalDiscountAmount(
        items,
        discount?.globalDiscountType,
        discount?.globalDiscountValue,
    );
    return round(rawTotalHT - discountAmount);
};

// ---------- TOTAL TTC ----------
// Must match exactly the backend calculs.ts getTotalTTC function (group by VAT rate)
export const getTotalTTC = (
    items: any[],
    options?: {
        isCustomerHT?: boolean;
    } & GlobalDiscount,
) => {
    const rawTotalHT = getRawTotalHT(items);
    const discountAmount = getGlobalDiscountAmount(
        items,
        options?.globalDiscountType,
        options?.globalDiscountValue,
    );
    if (options?.isCustomerHT) return round(rawTotalHT - discountAmount);

    const differentVatRates = items.reduce((acc: any, item: any) => {
        if (acc.indexOf(item.vat) === -1) acc.push(item.vat);
        return acc;
    }, []);

    // Global discount is distributed proportionally across VAT rate groups
    // (each group's share of the raw HT total) before applying that rate's VAT.
    let totalVAT = 0;
    for (const vatRate of differentVatRates) {
        const rateHT = items.reduce(
            (acc: number, item: any) =>
                acc + (item.vat === vatRate ? getInvoiceLineTotalHT([item]) : 0),
            0,
        );
        const rateShareOfDiscount = rawTotalHT !== 0 ? discountAmount * (rateHT / rawTotalHT) : 0;
        const rateNetHT = rateHT - rateShareOfDiscount;
        totalVAT += round(rateNetHT * (toNumber(vatRate) / 100));
    }

    const netTotalHT = round(rawTotalHT - discountAmount);
    return round(netTotalHT + totalVAT);
};

// ---------- TOTAL MARGIN ----------
export const getTotalMargin = (items: any) => {
    let totalPurchasePrice = items.reduce(
        (acc: number, item: any) =>
            acc + (toNumber(item.totalPurchasePrice) * toNumber(item.quantity) || 0),
        0
    );

    let totalSellPrice = getTotalHT(items);

    const itemWithoutTotalPurchasePrice = items.find(
        (item: any) =>
            (item.totalPurchasePrice === undefined ||
                item.totalPurchasePrice === null) &&
            !item._id?.includes("custom")
    );

    if (itemWithoutTotalPurchasePrice) return 0;
    if (!totalPurchasePrice) return 0;

    return totalSellPrice - totalPurchasePrice;
};

export { round };
